// Media blocks: image, video, embedded page and map.
import { registerBlock } from '../core/registry.js';
import { replaceClasses, findClass } from '../core/dom.js';
import { safeUrl } from '../core/sanitize.js';
import { readStyle, writeStyle, ROUNDED_CLASS, ROUNDED_OPTIONS, SHADOW_CLASS } from '../core/styles.js';

// ---- Image -----------------------------------------------------------------------------------------

const IMAGE_ALIGN_CLASS = /^(?:float-(?:start|end)|mx-auto|d-block|d-table|me-3|ms-3)$/;

function image(el) {
    return el.querySelector('img');
}

registerBlock({
    name: 'image',
    label: 'block.image',
    icon: 'image',
    category: 'media',
    priority: 70,
    match: el => (el.localName === 'figure' && el.classList.contains('pflow-img'))
        || el.localName === 'img'
        || (el.localName === 'a' && el.children.length === 1 && el.firstElementChild.matches('figure.pflow-img, img')),
    // Bare images and linked figures are normalized: the block element is always the <figure>,
    // its link is kept in data-pf-href while editing and put back around it at export.
    normalize: (el, { doc }) => {
        let link = null;
        let node = el;
        if (el.localName === 'a') {
            link = el;
            node = el.firstElementChild;
        }
        if (node.localName === 'img') {
            const figure = doc.createElement('figure');
            figure.className = 'figure image pflow-img';
            node.replaceWith(figure);
            figure.append(node);
            node = figure;
        }
        if (link) {
            node.setAttribute('data-pf-href', link.getAttribute('href') || '');
            if (link.getAttribute('target')) node.setAttribute('data-pf-target', link.getAttribute('target'));
            link.replaceWith(node);
        }
        return node;
    },
    exportNode: (el, { doc }) => {
        const href = el.getAttribute('data-pf-href');
        const target = el.getAttribute('data-pf-target');
        el.removeAttribute('data-pf-href');
        el.removeAttribute('data-pf-target');
        if (href) {
            const a = doc.createElement('a');
            a.setAttribute('href', href);
            if (target) {
                a.setAttribute('target', target);
                if (target === '_blank') a.setAttribute('rel', 'noopener');
            }
            el.replaceWith(a);
            a.append(el);
            return a;
        }
        return el;
    },
    create: ({ h }) => h('figure', { class: 'figure image pflow-img' }, h('img', { class: 'figure-img img-fluid rounded', src: '', alt: '' })),
    fields: [
        {
            type: 'media',
            label: 'field.image',
            mediaType: 'image',
            get: el => (image(el) ? image(el).getAttribute('src') || '' : ''),
            set: (el, value, ctx, media) => {
                const img = image(el);
                const url = safeUrl(value, { image: true });
                if (!img) return;
                img.setAttribute('src', url);
                img.removeAttribute('srcset');
                if (media && !img.getAttribute('alt') && (media.alt || media.label)) img.setAttribute('alt', media.alt || media.label);
            },
        },
        {
            type: 'text',
            label: 'field.alt',
            help: 'help.alt',
            get: el => (image(el) ? image(el).getAttribute('alt') || '' : ''),
            set: (el, value) => image(el) && image(el).setAttribute('alt', value),
        },
        {
            type: 'link',
            label: 'field.link',
            get: el => ({ url: el.getAttribute('data-pf-href') || '', newTab: el.getAttribute('data-pf-target') === '_blank' }),
            set: (el, value) => {
                const url = safeUrl(value.url);
                if (url) el.setAttribute('data-pf-href', url);
                else el.removeAttribute('data-pf-href');
                if (url && value.newTab) el.setAttribute('data-pf-target', '_blank');
                else el.removeAttribute('data-pf-target');
            },
        },
        {
            type: 'toggle',
            label: 'field.caption',
            get: el => !!el.querySelector('figcaption'),
            set: (el, value, ctx) => {
                const caption = el.querySelector('figcaption');
                if (value && !caption) {
                    const fc = ctx.doc.createElement('figcaption');
                    fc.className = 'figure-caption';
                    fc.setAttribute('data-pf-edit', 'inline');
                    fc.textContent = ctx.t('placeholder.caption');
                    el.append(fc);
                } else if (!value && caption) caption.remove();
            },
        },
        {
            type: 'select',
            label: 'field.width',
            options: [{ value: '', label: 'option.auto' }, ...['25', '33', '50', '66', '75', '100'].map(value => ({ value: `${value}%`, label: `${value}%` }))],
            get: el => readStyle(el, 'width'),
            set: (el, value) => {
                writeStyle(el, 'width', value);
                const img = image(el);
                if (img) writeStyle(img, 'width', value ? '100%' : '');
            },
        },
        {
            type: 'segmented',
            label: 'field.align',
            icons: true,
            options: [
                { value: '', label: 'option.default', icon: 'x' },
                { value: 'float-start me-3', label: 'option.wrapLeft', icon: 'text-align-left' },
                { value: 'd-table mx-auto', label: 'option.center', icon: 'text-align-center' },
                { value: 'float-end ms-3', label: 'option.wrapRight', icon: 'text-align-right' },
            ],
            get: el => {
                if (el.classList.contains('float-start')) return 'float-start me-3';
                if (el.classList.contains('float-end')) return 'float-end ms-3';
                if (el.classList.contains('mx-auto')) return 'd-table mx-auto';
                return '';
            },
            set: (el, value) => replaceClasses(el, IMAGE_ALIGN_CLASS, value),
            pattern: IMAGE_ALIGN_CLASS,
        },
        {
            type: 'select',
            label: 'field.ratio',
            options: [{ value: '', label: 'option.original' }, { value: '1/1', label: '1:1' }, { value: '4/3', label: '4:3' }, { value: '3/2', label: '3:2' }, { value: '16/9', label: '16:9' }, { value: '21/9', label: '21:9' }, { value: '3/4', label: '3:4' }],
            target: image,
            get: el => readStyle(el, 'aspect-ratio').replace(/\s/g, ''),
            set: (el, value) => {
                writeStyle(el, 'aspect-ratio', value.replace('/', ' / '));
                writeStyle(el, 'object-fit', value ? 'cover' : '');
                if (value) writeStyle(el, 'width', '100%');
            },
        },
        {
            type: 'select',
            label: 'field.corners',
            options: ROUNDED_OPTIONS,
            target: image,
            get: el => findClass(el, ROUNDED_CLASS),
            set: (el, value) => replaceClasses(el, ROUNDED_CLASS, value),
            pattern: ROUNDED_CLASS,
        },
        {
            type: 'select',
            label: 'field.shadow',
            options: [{ value: '', label: 'option.none' }, { value: 'shadow-sm', label: 'option.small' }, { value: 'shadow', label: 'option.medium' }, { value: 'shadow-lg', label: 'option.large' }],
            target: image,
            get: el => findClass(el, SHADOW_CLASS),
            set: (el, value) => replaceClasses(el, SHADOW_CLASS, value),
            pattern: SHADOW_CLASS,
        },
    ],
});

// ---- Video -----------------------------------------------------------------------------------------

const RATIO_CLASS = /^ratio(?:-(?:1x1|4x3|16x9|21x9|9x16))?$/;

/** YouTube and Vimeo URLs to their embed address; null for any other URL. */
export function videoEmbed(url, { privacy = true } = {}) {
    const value = String(url || '').trim();
    let match = value.match(/(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
    if (match) {
        const short = /\/shorts\//.test(value);
        const start = value.match(/[?&](?:t|start)=(\d+)/);
        const params = new URLSearchParams();
        if (start) params.set('start', start[1]);
        const query = params.toString();
        return {
            provider: 'youtube',
            id: match[1],
            vertical: short,
            src: `https://www.${privacy ? 'youtube-nocookie' : 'youtube'}.com/embed/${match[1]}${query ? '?' + query : ''}`,
        };
    }
    match = value.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (match) return { provider: 'vimeo', id: match[1], vertical: false, src: `https://player.vimeo.com/video/${match[1]}?dnt=1` };
    return null;
}

function videoSource(el) {
    const iframe = el.querySelector('iframe');
    if (iframe) return iframe.getAttribute('src') || '';
    const video = el.querySelector('video');
    if (!video) return '';
    const source = video.querySelector('source');
    return video.getAttribute('src') || (source ? source.getAttribute('src') : '') || '';
}

function videoFlag(name) {
    return {
        type: 'toggle',
        label: `field.${name}`,
        when: el => !!el.querySelector('video'),
        get: el => el.querySelector('video').hasAttribute(name),
        set: (el, value) => {
            const video = el.querySelector('video');
            video.toggleAttribute(name, !!value);
            if (name === 'autoplay' && value) {
                video.setAttribute('muted', '');
                video.setAttribute('playsinline', '');
            }
        },
    };
}

export function setVideoSource(el, url, doc, options = {}) {
    const embed = videoEmbed(url, options);
    el.querySelectorAll('video, iframe').forEach(node => node.remove());
    if (embed) {
        const iframe = doc.createElement('iframe');
        iframe.setAttribute('src', embed.src);
        iframe.setAttribute('title', embed.provider === 'youtube' ? 'YouTube video' : 'Vimeo video');
        iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('loading', 'lazy');
        el.prepend(iframe);
        replaceClasses(el, RATIO_CLASS, ['ratio', embed.vertical ? 'ratio-9x16' : (findClass(el, /^ratio-/) || 'ratio-16x9')]);
        return;
    }
    replaceClasses(el, RATIO_CLASS, []);
    const video = doc.createElement('video');
    video.setAttribute('controls', '');
    video.setAttribute('preload', 'metadata');
    video.setAttribute('playsinline', '');
    video.className = 'w-100 h-auto';
    const clean = safeUrl(url);
    if (clean) {
        const source = doc.createElement('source');
        source.setAttribute('src', clean);
        source.setAttribute('type', /\.webm(?:$|\?)/i.test(clean) ? 'video/webm' : 'video/mp4');
        video.append(source);
    }
    el.prepend(video);
}

registerBlock({
    name: 'video',
    label: 'block.video',
    icon: 'video',
    category: 'media',
    priority: 70,
    match: el => (el.localName === 'figure' && el.classList.contains('pflow-video'))
        || el.localName === 'video'
        || (el.localName === 'iframe' && !!videoEmbed(el.getAttribute('src'))),
    normalize: (el, { doc }) => {
        if (el.localName === 'figure') {
            // PageFlow 1.x kept <video> tags pointing to YouTube: they become real embeds.
            const video = el.querySelector('video');
            const source = video ? (video.getAttribute('src') || (video.querySelector('source') || {}).getAttribute?.('src') || '') : '';
            if (video && videoEmbed(source)) setVideoSource(el, source, doc);
            return el;
        }
        const figure = doc.createElement('figure');
        figure.className = 'figure pflow-video w-100';
        el.replaceWith(figure);
        if (el.localName === 'iframe') {
            const embed = videoEmbed(el.getAttribute('src'));
            figure.append(el);
            replaceClasses(figure, RATIO_CLASS, ['ratio', embed && embed.vertical ? 'ratio-9x16' : 'ratio-16x9']);
        } else {
            figure.append(el);
        }
        return figure;
    },
    create: ({ h }) => h('figure', { class: 'figure pflow-video w-100' },
        h('video', { controls: '', preload: 'metadata', playsinline: '', class: 'w-100 h-auto' })),
    fields: [
        {
            type: 'media',
            label: 'field.videoUrl',
            mediaType: 'video',
            help: 'help.video',
            get: videoSource,
            set: (el, value, ctx) => setVideoSource(el, value, ctx.doc, { privacy: ctx.options.videoPrivacy !== false }),
        },
        {
            type: 'select',
            label: 'field.ratio',
            when: el => !!el.querySelector('iframe'),
            options: [{ value: 'ratio-16x9', label: '16:9' }, { value: 'ratio-4x3', label: '4:3' }, { value: 'ratio-1x1', label: '1:1' }, { value: 'ratio-21x9', label: '21:9' }, { value: 'ratio-9x16', label: '9:16' }],
            get: el => findClass(el, /^ratio-/) || 'ratio-16x9',
            set: (el, value) => replaceClasses(el, RATIO_CLASS, ['ratio', value]),
            pattern: RATIO_CLASS,
        },
        videoFlag('controls'),
        videoFlag('autoplay'),
        videoFlag('loop'),
        videoFlag('muted'),
    ],
});

// ---- Embedded page and map ---------------------------------------------------------------------------

function frameHeightField() {
    return {
        type: 'range',
        label: 'field.height',
        min: 120,
        max: 900,
        step: 10,
        unit: 'px',
        get: el => parseInt((el.querySelector('iframe') || el).getAttribute('height'), 10) || 400,
        set: (el, value) => {
            const iframe = el.querySelector('iframe');
            if (iframe) iframe.setAttribute('height', String(parseInt(value, 10)));
        },
    };
}

registerBlock({
    name: 'embed',
    label: 'block.embed',
    icon: 'frame-corners',
    category: 'media',
    priority: 65,
    match: el => (el.localName === 'figure' && el.classList.contains('pflow-iframe'))
        || (el.localName === 'iframe' && !videoEmbed(el.getAttribute('src')) && !/google\.[a-z.]+\/maps/.test(el.getAttribute('src') || '')),
    normalize: (el, { doc }) => {
        if (el.localName === 'figure') return el;
        const figure = doc.createElement('figure');
        figure.className = 'figure pflow-iframe w-100';
        el.replaceWith(figure);
        figure.append(el);
        return figure;
    },
    create: ({ h }) => h('figure', { class: 'figure pflow-iframe w-100' },
        h('iframe', { src: '', width: '100%', height: '400', loading: 'lazy', class: 'w-100 border-0', title: 'Embedded content' })),
    fields: [
        {
            type: 'url',
            label: 'field.url',
            help: 'help.embed',
            get: el => (el.querySelector('iframe') || { getAttribute: () => '' }).getAttribute('src') || '',
            set: (el, value) => {
                const iframe = el.querySelector('iframe');
                const url = safeUrl(value);
                if (iframe && (url === '' || /^(?:https?:)?\/\//i.test(url))) iframe.setAttribute('src', url);
            },
        },
        frameHeightField(),
    ],
});

export function mapUrl(address, zoom = 14) {
    return `https://maps.google.com/maps?q=${encodeURIComponent(address)}&z=${parseInt(zoom, 10) || 14}&output=embed`;
}

function mapParams(el) {
    const iframe = el.querySelector('iframe');
    const src = iframe ? iframe.getAttribute('src') || '' : '';
    const query = src.includes('?') ? new URLSearchParams(src.split('?')[1]) : new URLSearchParams();
    return { address: query.get('q') || '', zoom: query.get('z') || '14' };
}

registerBlock({
    name: 'map',
    label: 'block.map',
    icon: 'map-trifold',
    category: 'media',
    priority: 66,
    match: el => (el.localName === 'figure' && el.classList.contains('pflow-map'))
        || (el.localName === 'iframe' && /google\.[a-z.]+\/maps/.test(el.getAttribute('src') || '')),
    normalize: (el, { doc }) => {
        if (el.localName === 'figure') return el;
        const figure = doc.createElement('figure');
        figure.className = 'figure pflow-map w-100';
        el.replaceWith(figure);
        figure.append(el);
        return figure;
    },
    create: ({ h, t }) => h('figure', { class: 'figure pflow-map w-100' },
        h('iframe', { src: mapUrl(t('placeholder.address')), width: '100%', height: '360', loading: 'lazy', class: 'w-100 border-0 rounded-3', title: 'Map', referrerpolicy: 'no-referrer-when-downgrade' })),
    fields: [
        {
            type: 'text',
            label: 'field.address',
            get: el => mapParams(el).address,
            set: (el, value) => {
                const iframe = el.querySelector('iframe');
                if (iframe) iframe.setAttribute('src', mapUrl(value, mapParams(el).zoom));
            },
            lazy: true,
        },
        {
            type: 'range',
            label: 'field.zoom',
            min: 3,
            max: 20,
            get: el => parseInt(mapParams(el).zoom, 10),
            set: (el, value) => {
                const iframe = el.querySelector('iframe');
                if (iframe) iframe.setAttribute('src', mapUrl(mapParams(el).address, value));
            },
            lazy: true,
        },
        frameHeightField(),
    ],
});
